import { useCallback, useEffect, useMemo } from 'react'

import {
  Avatar,
  Card,
  Divider,
  Flex,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { useGetList, useTranslate } from 'react-admin'
import { useGetApiVideoPromotionChannelsYoutubeChannelIdPlaylists } from '../../../../codegen/generated/video-promotion'
import { VideoPromotionHelper } from '../../../../helpers/VideoPromotion.helper'
import { useVideoPromotionStore } from '../../../../stores/useVideoPromotionStore'
import { YoutubeChannel } from '../../../../types/YoutubeChannel'
import {
  IPromotionEndscreenSlotChoice,
  IPromotionEndscreenSlotType,
  NewPromotionLayoutId,
} from '../../../../types/components/VideoPromotion.type'
import { NewPromotionCard } from '../../NewPromotionCard/NewPromotionCard'
import { PromotionTargetVideoSelect } from '../PromotionTargetVideoSelect'

const DEFAULT_START_SECONDS_BEFORE_END = 20

export const PromotionChooseLayout = () => {
  const t = useTranslate()
  const videoPromotionStore = useVideoPromotionStore()

  const layout = useMemo(
    () =>
      VideoPromotionHelper.LAYOUTS.find(
        l => l.id === (videoPromotionStore.endScreenLayoutId ?? 'A'),
      ),
    [videoPromotionStore.endScreenLayoutId],
  )

  useEffect(() => {
    if (!videoPromotionStore.endScreenLayoutId) {
      videoPromotionStore.setEndscreenLayoutId('A')
    }
  }, [videoPromotionStore])

  const config = videoPromotionStore.elementsConfiguration.endScreen.config
  const startSecondsBeforeEnd =
    config?.mode === 'custom' ? config.startSecondsBeforeEnd : DEFAULT_START_SECONDS_BEFORE_END
  const durationSeconds = config?.mode === 'custom' ? config.durationSeconds : undefined

  const youtubeChannelsQuery = useGetList<YoutubeChannel>('youtubeChannels', {
    sort: { field: 'name', order: 'ASC' },
    pagination: { page: 1, perPage: 1000 },
  })
  const youtubeChannels = useMemo(
    () =>
      (youtubeChannelsQuery.data ?? []).map(b => ({
        value: b.id,
        label: b.name,
      })),
    [youtubeChannelsQuery.data],
  )

  const playlistsQuery = useGetApiVideoPromotionChannelsYoutubeChannelIdPlaylists(
    videoPromotionStore.youtubeChannelId ?? '',
    {
      query: {
        enabled: !!videoPromotionStore.youtubeChannelId,
      },
    },
  )
  const playlists = useMemo(
    () =>
      (playlistsQuery.data?.body.playlists ?? []).map(b => ({
        value: b.id,
        label: b.title,
      })),
    [playlistsQuery.data],
  )

  const updateConfig = useCallback(
    (
      layoutId: NewPromotionLayoutId,
      choices: IPromotionEndscreenSlotChoice[],
      start: number,
      duration: number | undefined,
    ) => {
      const nextLayout = VideoPromotionHelper.LAYOUTS.find(l => l.id === layoutId)
      if (!nextLayout) {
        return
      }
      videoPromotionStore.setEndscreenConfig(
        VideoPromotionHelper.buildEndscreenConfig(nextLayout, choices, start, duration),
      )
    },
    [videoPromotionStore],
  )

  const onSelectLayout = useCallback(
    (layoutId: NewPromotionLayoutId) => {
      videoPromotionStore.setEndscreenLayoutId(layoutId)
      const nextLayout = VideoPromotionHelper.LAYOUTS.find(l => l.id === layoutId)
      if (!nextLayout) {
        return
      }
      updateConfig(
        layoutId,
        videoPromotionStore.endScreenSlotChoices.slice(0, nextLayout.slots.length - 1),
        startSecondsBeforeEnd,
        durationSeconds,
      )
    },
    [videoPromotionStore, updateConfig, startSecondsBeforeEnd, durationSeconds],
  )

  const onSlotTypeChange = useCallback(
    (slotIndex: number, slotType: IPromotionEndscreenSlotType | undefined) => {
      videoPromotionStore.setEndscreenSlotChoice(slotIndex, {
        type: slotType ?? 'recentUpload',
      })
    },
    [videoPromotionStore],
  )

  const onSlotTargetChange = useCallback(
    (slotIndex: number, targetId: string | undefined) => {
      const choice = videoPromotionStore.endScreenSlotChoices[slotIndex]
      if (!choice) {
        return
      }
      const next =
        choice.type === 'videoFixed'
          ? { ...choice, videoId: targetId }
          : choice.type === 'playlist'
            ? { ...choice, playlistId: targetId }
            : { ...choice, channelId: targetId }
      videoPromotionStore.setEndscreenSlotChoice(slotIndex, next)
    },
    [videoPromotionStore],
  )

  const slotTypeOptions: { value: IPromotionEndscreenSlotType; label: string }[] = [
    { value: 'videoFixed', label: t('pages.videoPromotion.endScreen.slotTypes.videoFixed') },
    { value: 'recentUpload', label: t('pages.videoPromotion.endScreen.slotTypes.recentUpload') },
    { value: 'bestForViewer', label: t('pages.videoPromotion.endScreen.slotTypes.bestForViewer') },
    { value: 'playlist', label: t('pages.videoPromotion.endScreen.slotTypes.playlist') },
    { value: 'channel', label: t('pages.videoPromotion.endScreen.slotTypes.channel') },
  ]

  return (
    <Stack gap="xl" mt="sm">
      <Stack gap="xs">
        <Title order={5}>{t('pages.videoPromotion.endScreen.layout')}</Title>
        <SimpleGrid cols={3}>
          {VideoPromotionHelper.LAYOUTS.map(l => (
            <NewPromotionCard
              key={l.id}
              active={l.id === layout?.id}
              onClick={() => onSelectLayout(l.id)}
            >
              <Stack align="center" gap="xs">
                <Flex align="center" justify="center" h={70} px="xs">
                  <SimpleGrid cols={l.slots.length === 3 ? 3 : 2} spacing="xs" verticalSpacing={4}>
                    {l.slots.map((s, idx) =>
                      idx === l.slots.length - 1 ? (
                        <Avatar size="md" radius="xl" color="lime" key={`${s.left}-${s.top}`}>
                          <Text size="xs" fw="bold">
                            Abo
                          </Text>
                        </Avatar>
                      ) : (
                        <Flex
                          align="center"
                          justify="center"
                          w={50}
                          h={30}
                          bg="cyan.0"
                          bdrs="sm"
                          key={`${s.left}-${s.top}`}
                        >
                          <Text size="xs" fw={700} c="cyan" w="100%" ta="center">
                            Video
                          </Text>
                        </Flex>
                      ),
                    )}
                  </SimpleGrid>
                </Flex>
                <Divider w="80%" />
                <Title order={6} size="sm" fw={400} w="100%" ta="center">
                  {t(
                    `pages.videoPromotion.endScreen.layouts.${
                      l.id === 'A' ? 0 : l.id === 'B' ? 1 : 2
                    }`,
                  )}
                </Title>
              </Stack>
            </NewPromotionCard>
          ))}
        </SimpleGrid>
      </Stack>

      {/** Emplacements */}
      <Stack gap="xs">
        <Title order={5}>{t('pages.videoPromotion.endScreen.positions')}</Title>
        <SimpleGrid cols={Math.min(layout?.slots.length ?? 2, 4)}>
          {layout?.slots.map((s, idx) => {
            const isSubscribe = idx === layout.slots.length - 1
            const choice = videoPromotionStore.endScreenSlotChoices[idx]
            const typeLabel = choice && slotTypeOptions.find(o => o.value === choice.type)?.label
            return (
              <Card key={`${s.left}-${s.top}`} withBorder>
                <Stack align="center" gap="xs">
                  <Flex align="center" justify="center" h={70} px="xs">
                    {isSubscribe ? (
                      <Avatar size="lg" radius="xl" color="lime">
                        <Text size="sm" fw="bold">
                          Abo
                        </Text>
                      </Avatar>
                    ) : (
                      <Flex
                        align="center"
                        justify="center"
                        w={110}
                        h={62}
                        bg="cyan.0"
                        bdrs="sm"
                        p={4}
                      >
                        <Title order={6} c="cyan" w="100%" ta="center" lineClamp={2}>
                          {typeLabel ?? t('pages.videoPromotion.endScreen.slotLabel')}
                        </Title>
                      </Flex>
                    )}
                  </Flex>
                  <Divider w="80%" />
                  <Title order={6} size="md" fw={400} w="100%" ta="center">
                    {isSubscribe
                      ? t('pages.videoPromotion.endScreen.slotTypes.subscribe')
                      : `${t('pages.videoPromotion.endScreen.slotLabel')} ${idx + 1}`}
                  </Title>
                  {!isSubscribe && (
                    <>
                      <Select
                        placeholder={t('pages.videoPromotion.endScreen.slotTypePlaceholder')}
                        data={slotTypeOptions}
                        value={choice?.type}
                        onChange={st => onSlotTypeChange(idx, st as IPromotionEndscreenSlotType)}
                        clearable
                        w="100%"
                      />
                      {choice?.type === 'videoFixed' && (
                        <PromotionTargetVideoSelect
                          value={choice.videoId}
                          onChange={video => onSlotTargetChange(idx, video?.id)}
                        />
                      )}
                      {choice?.type === 'playlist' && (
                        <Select
                          placeholder={t('pages.videoPromotion.playlistName')}
                          data={playlists}
                          value={choice.playlistId}
                          onChange={pId => onSlotTargetChange(idx, pId ?? undefined)}
                          searchable
                          nothingFoundMessage={t('pages.videoPromotion.noPlaylist')}
                          w="100%"
                        />
                      )}
                      {choice?.type === 'channel' && (
                        <Select
                          placeholder={t('_generics.youtubeChannel')}
                          data={youtubeChannels}
                          value={choice.channelId}
                          onChange={cId => onSlotTargetChange(idx, cId ?? undefined)}
                          searchable
                          w="100%"
                        />
                      )}
                    </>
                  )}
                </Stack>
              </Card>
            )
          })}
        </SimpleGrid>
      </Stack>

      {/** Parameters */}
      <Stack gap="xs">
        <Title order={5}>{t('pages.videoPromotion.endScreen.parameters')}</Title>
        <SimpleGrid cols={2}>
          <Stack gap={3}>
            <Group align="center" gap="xs">
              <NumberInput
                label={
                  <Text size="sm" w={150}>
                    {t('pages.videoPromotion.endScreen.startSecondsBeforeEnd')}
                  </Text>
                }
                name="startSecondsBeforeEnd"
                value={startSecondsBeforeEnd}
                min={0}
                w={110}
                onChange={value =>
                  layout &&
                  updateConfig(
                    layout.id,
                    videoPromotionStore.endScreenSlotChoices,
                    Number(value ?? DEFAULT_START_SECONDS_BEFORE_END),
                    durationSeconds,
                  )
                }
              />
              <Text c="dimmed" mt="lg" fw={500}>
                {t('pages.videoPromotion.endScreen.secondsBeforeEnd')}
              </Text>
            </Group>
            <Text size="xs" c="dimmed">
              {t('pages.videoPromotion.endScreen.defaultValueSeconds')}
            </Text>
          </Stack>
          <Group align="center" gap="xs" h="100%">
            <NumberInput
              label={
                <Text size="sm" w={150}>
                  {t('pages.videoPromotion.endScreen.durationSeconds')}
                </Text>
              }
              name="durationSeconds"
              value={durationSeconds}
              min={1}
              w={110}
              onChange={value =>
                layout &&
                updateConfig(
                  layout.id,
                  videoPromotionStore.endScreenSlotChoices,
                  startSecondsBeforeEnd,
                  value === '' || value === undefined ? undefined : Number(value),
                )
              }
            />
            <Text c="dimmed" mt="lg" fw={500}>
              {t('_generics.seconds')}
            </Text>
          </Group>
        </SimpleGrid>
      </Stack>
    </Stack>
  )
}
